import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";

const AddVendor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";

  // Check if we're editing an existing vendor
  const isEdit = location.pathname.includes("edit");
  const existingVendor = location.state?.vendor;

  // Form state
  const [formData, setFormData] = useState({
    vendor_firm_name: "",
    vendor_name: "",
    vendor_mobile: "",
    vendor_email: "",
    vendor_address: "",
    vendor_gst_no: "",
    vendor_outstanding: "0",
    vendor_opening_balance: "0", // NEW: Opening balance field
    vendor_opening_balance_type: "credit", // NEW: credit/debit type
    vendor_grage_id: grage_id,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Populate form if editing
  useEffect(() => {
    if (isEdit && existingVendor) {
      setFormData({
        vendor_firm_name: existingVendor.vendor_firm_name || "",
        vendor_name: existingVendor.vendor_name || "",
        vendor_mobile: existingVendor.vendor_mobile || "",
        vendor_email: existingVendor.vendor_email || "",
        vendor_address: existingVendor.vendor_address || "",
        vendor_gst_no: existingVendor.vendor_gst_no || "",
        vendor_outstanding: existingVendor.vendor_outstanding || "0",
        vendor_opening_balance: "0", // Not editable in edit mode
        vendor_opening_balance_type: "credit", // Default
        vendor_grage_id: existingVendor.vendor_grage_id || grage_id,
      });
    } else {
      setFormData(prev => ({
        ...prev,
        vendor_grage_id: grage_id,
      }));
    }
  }, [isEdit, existingVendor, grage_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validate opening balance to allow only numbers and decimal
    if (name === "vendor_opening_balance") {
      // Allow only numbers and one decimal point
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.vendor_firm_name.trim()) {
      newErrors.vendor_firm_name = "Firm name is required";
    }
    
    if (!formData.vendor_name.trim()) {
      newErrors.vendor_name = "Contact person name is required";
    }
    
    if (!formData.vendor_mobile.trim()) {
      newErrors.vendor_mobile = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(formData.vendor_mobile)) {
      newErrors.vendor_mobile = "Please enter a valid 10-digit mobile number";
    }
    
    if (formData.vendor_email && !/^\S+@\S+\.\S+$/.test(formData.vendor_email)) {
      newErrors.vendor_email = "Please enter a valid email address";
    }
    
    if (formData.vendor_gst_no && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.vendor_gst_no)) {
      newErrors.vendor_gst_no = "Please enter a valid GST number";
    }
    
    // Validate opening balance
    if (formData.vendor_opening_balance !== "" && !/^\d+(\.\d{1,2})?$/.test(formData.vendor_opening_balance)) {
      newErrors.vendor_opening_balance = "Please enter a valid amount (max 2 decimal places)";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length == 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }
    
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("company_id", company_id);
      formDataToSend.append("vendor_firm_name", formData.vendor_firm_name);
      formDataToSend.append("vendor_name", formData.vendor_name);
      formDataToSend.append("vendor_mobile", formData.vendor_mobile);
      formDataToSend.append("vendor_email", formData.vendor_email || "");
      formDataToSend.append("vendor_address", formData.vendor_address || "");
      formDataToSend.append("vendor_gst_no", formData.vendor_gst_no || "");
      formDataToSend.append("vendor_outstanding", formData.vendor_outstanding || "0");
      
      // NEW: Send opening balance data
      if (!isEdit) {
        formDataToSend.append("vendor_opening_balance", formData.vendor_opening_balance || "0");
        formDataToSend.append("vendor_opening_balance_type", formData.vendor_opening_balance_type || "credit");
      }
      
      formDataToSend.append("vendor_grage_id", formData.vendor_grage_id);
      
      // Add action and vendor_id if editing
      if (isEdit && existingVendor) {
        formDataToSend.append("action", "update");
        formDataToSend.append("vendor_id", existingVendor.vendor_id);
      } else {
        formDataToSend.append("action", "add");
      }
      
      const response = await axios.post(
        `${server_url}vendor.php`,
        formDataToSend,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      
      if (response.data.status == "1") {
        toast.success(response.data.error || (isEdit ? "Vendor updated successfully!" : "Vendor added successfully!"));
        navigate("/vendors");
      } else {
        toast.error(response.data.error || `Failed to ${isEdit ? 'update' : 'add'} vendor`);
      }
    } catch (error) {
      console.error("Save vendor error:", error);
      toast.error(`Unable to ${isEdit ? 'update' : 'add'} vendor. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  // If no existing data in edit mode
  if (isEdit && !existingVendor) {
    return (
      <div className="login-container">
        <div className="login-form-container">
          <div style={{ textAlign: "center", padding: "40px" }}>
            <h3>No vendor data found</h3>
            <p>Please go back and select a vendor to edit.</p>
            <Link
              to="/vendors"
              style={{ color: "#667eea", textDecoration: "none" }}
            >
              <i className="fas fa-arrow-left"></i> Back to Vendors
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form-container">
        <div className="login-form-wrapper">
          <form className="login-form" onSubmit={handleSubmit}>
            {/* Firm Name */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "0px",
                  fontWeight: "bold",
                }}
              >
                Firm Name *
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-building"></i>
                </div>
                <input
                  type="text"
                  name="vendor_firm_name"
                  className={`form-input ${errors.vendor_firm_name ? "error" : ""}`}
                  placeholder="Enter firm/business name"
                  value={formData.vendor_firm_name}
                  onChange={handleChange}
                  required
                />
              </div>
              {errors.vendor_firm_name && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.vendor_firm_name}
                </div>
              )}
            </div>

            {/* Contact Person */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "0px",
                  fontWeight: "bold",
                }}
              >
                Contact Person Name *
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-user"></i>
                </div>
                <input
                  type="text"
                  name="vendor_name"
                  className={`form-input ${errors.vendor_name ? "error" : ""}`}
                  placeholder="Enter contact person name"
                  value={formData.vendor_name}
                  onChange={handleChange}
                  required
                />
              </div>
              {errors.vendor_name && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.vendor_name}
                </div>
              )}
            </div>

            {/* Mobile Number */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "0px",
                  fontWeight: "bold",
                }}
              >
                Mobile Number *
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-phone"></i>
                </div>
                <input
                  type="tel"
                  name="vendor_mobile"
                  className={`form-input ${errors.vendor_mobile ? "error" : ""}`}
                  placeholder="Enter 10-digit mobile number"
                  value={formData.vendor_mobile}
                  onChange={handleChange}
                  maxLength="10"
                  required
                />
              </div>
              {errors.vendor_mobile && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.vendor_mobile}
                </div>
              )}
            </div>

            {/* Email */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "0px",
                  fontWeight: "bold",
                }}
              >
                Email Address
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <input
                  type="email"
                  name="vendor_email"
                  className={`form-input ${errors.vendor_email ? "error" : ""}`}
                  placeholder="Enter email address"
                  value={formData.vendor_email}
                  onChange={handleChange}
                />
              </div>
              {errors.vendor_email && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.vendor_email}
                </div>
              )}
            </div>

            {/* GST Number */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "0px",
                  fontWeight: "bold",
                }}
              >
                GST Number
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-file-invoice"></i>
                </div>
                <input
                  type="text"
                  name="vendor_gst_no"
                  className={`form-input ${errors.vendor_gst_no ? "error" : ""}`}
                  placeholder="Enter GST number (optional)"
                  value={formData.vendor_gst_no}
                  onChange={handleChange}
                  style={{ textTransform: "uppercase" }}
                />
              </div>
              {errors.vendor_gst_no && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.vendor_gst_no}
                </div>
              )}
            </div>

            {/* Opening Balance (only for new vendors) */}
            {!isEdit && (
              <>
                <div className="form-group">
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "0px",
                      fontWeight: "bold",
                    }}
                  >
                    Opening Balance
                  </label>
                  <div className="input-group">
                    <div className="input-icon">
                      <i className="fas fa-rupee-sign"></i>
                    </div>
                    <input
                      type="text"
                      name="vendor_opening_balance"
                      className={`form-input ${errors.vendor_opening_balance ? "error" : ""}`}
                      placeholder="Enter opening balance (0 if none)"
                      value={formData.vendor_opening_balance}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.vendor_opening_balance && (
                    <div className="error-message">
                      <i className="fas fa-exclamation-circle"></i>
                      {errors.vendor_opening_balance}
                    </div>
                  )}
                </div>

                <div className="form-group" style={{display:"none"}}>
                  <label
                    style={{
                      textAlign: "left",
                      display: "block",
                      marginBottom: "0px",
                      fontWeight: "bold",
                    }}
                  >
                    Balance Type
                  </label>
                  <div className="input-group" style={{ flexDirection: "row", gap: "20px", marginTop: "5px" }}>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="vendor_opening_balance_type"
                        value="credit"
                        checked={formData.vendor_opening_balance_type === "credit"}
                        onChange={handleChange}
                        style={{ marginRight: "8px" }}
                      />
                      <i className="fas fa-arrow-down" style={{ color: "#10b981", marginRight: "5px" }}></i>
                      Credit (Vendor owes you)
                    </label>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="vendor_opening_balance_type"
                        value="debit"
                        checked={formData.vendor_opening_balance_type === "debit"}
                        onChange={handleChange}
                        style={{ marginRight: "8px" }}
                      />
                      <i className="fas fa-arrow-up" style={{ color: "#dc2626", marginRight: "5px" }}></i>
                      Debit (You owe vendor)
                    </label>
                  </div>
                  <small style={{ color: "#666", fontSize: "12px", marginTop: "5px" }}>
                    Credit: Vendor has outstanding balance (you need to collect)<br/>
                    Debit: You have payable to vendor (you need to pay)
                  </small>
                </div>
              </>
            )}

            {/* Address */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "5px",
                  fontWeight: "bold",
                }}
              >
                Address
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <textarea
                  name="vendor_address"
                  className="form-input textarea"
                  placeholder="Enter full address"
                  value={formData.vendor_address}
                  onChange={handleChange}
                  rows="3"
                />
              </div>
            </div>

            {/* Hidden garage_id */}
            <input
              type="hidden"
              name="vendor_grage_id"
              value={formData.vendor_grage_id}
            />

            {/* Submit Button */}
            <button
              type="submit"
              className="login-button"
              disabled={submitting}
            >
              <i className="fas fa-save"></i>
              {submitting
                ? "Saving..."
                : isEdit
                ? "Update Vendor"
                : "Add Vendor"}
            </button>
          </form>
        </div>
      </div>
      <style jsx>{`
        .login-container {
          min-height: 63vh;
          background: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .login-form-container {
          width: 100%;
          max-width: 600px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }
        @media (max-width: 768px) {
          .login-form-container {
            flex: none;
            padding: 0px;
          }
        }
        .login-form-wrapper {
          padding: 20px;
        }
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 0px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        label {
          font-weight: 600;
          color: #4a5568;
          font-size: 14px;
        }
        .input-group {
          position: relative;
          display: flex;
          align-items: center;
        }
        .input-icon {
          position: absolute;
          left: 12px;
          color: #a0aec0;
          z-index: 1;
          font-size: 16px;
        }
        .form-input {
          width: 100%;
          padding: 10px 12px 10px 40px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.3s;
          background: white;
        }
        .form-input:focus {
          outline: none;
          border-color: #0352a1;
          box-shadow: 0 0 0 3px rgba(3, 82, 161, 0.1);
        }
        .form-input.error {
          border-color: #dc2626;
        }
        .form-input.textarea {
          min-height: 80px;
          resize: vertical;
          padding-top: 10px;
          padding-bottom: 10px;
        }
        .form-input:disabled {
          background: #f8f9fa;
          cursor: not-allowed;
        }
        .error-message {
          color: #dc2626;
          font-size: 12px;
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .login-button {
          background: #0352a1;
          color: white;
          border: none;
          padding: 12px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 10px;
        }
        .login-button:hover:not(:disabled) {
          background: #024080;
        }
        .login-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        @media (max-width: 768px) {
          .login-form-wrapper {
            padding: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default AddVendor;